/**
 * ProfileSectionCard
 *
 * Reusable card wrapper for profile review sections. The edit pencil
 * is rendered only when an `onEdit` handler is supplied — this is the
 * single switch that turns the entire ProfileReview into a read-only
 * view (used by business-web's TalentDetail drawer) vs the editable
 * deep-link view (used by journey-web's Settings → Profile tab).
 */

import { useTranslation } from '@tonnie/locale';
import { LucideIcon, Edit2 } from '@tonnie/assets';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@tonnie/ui';

interface IProfileSectionCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  stepId: string;
  /**
   * Optional edit handler. When omitted, the pencil icon is hidden
   * and the section becomes read-only.
   */
  onEdit?: (stepId: string) => void;
  children: React.ReactNode;
}

export function ProfileSectionCard({
  icon: Icon,
  title,
  description,
  stepId,
  onEdit,
  children,
}: IProfileSectionCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon className="w-5 h-5 text-primary-accessible" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">{title}</CardTitle>
              <CardDescription className="text-xs mt-0.5">{description}</CardDescription>
            </div>
          </div>
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 hover:bg-primary/10"
              onClick={() => onEdit(stepId)}
              aria-label={t('common.edit')}
            >
              <Edit2 className="w-4 h-4 text-muted-foreground hover:text-primary-accessible" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}
